import React from "react";
import { useEffect, useState, useMemo, useCallback, useRef } from "react";
import Select from "react-select";
import axios from "axios";
import { BASE_URL } from "../utils/constants";
import Toast from "./Toast";
import Loader from "./Loader";
import debounce from "lodash/debounce";
import { decryptId } from "../utils/encrypt";
import { useDispatch, useSelector } from "react-redux";
import {
  addSingleFormulation,
  defaultFormulation,
  triggerRefresh,
} from "../utils/formulationSlice";
import cloneDeep from "lodash/cloneDeep";

const chemicalTypes = [
  "ORGANIC PIGMENT",
  "INORGANIC PIGMENT",
  "FILLER",
  "HARDNER",
  "EPOXY RESIN",
  "POLYSTER RESIN",
  "ADDITIVE",
];
const FormulationDetail = ({ id, goBackToList }) => {
  const dispatch = useDispatch();

  let formulation = useSelector((state) => state.formulation.selected);
  const actualId = id ? decryptId(decodeURIComponent(id)) : null;
  const [materials, setMaterials] = useState([]);
  const [selectedMaterials, setSelectedMaterials] = useState([]);
  const [chemicalTypeWeight, setChemicalTypeWeight] = useState([]);
  const inputRefs = useRef([]);
  const prevLengthRef = useRef(selectedMaterials.length);
  const scrollContainerRef = useRef(null);

  const [colourName, setColourName] = useState("");
  const [colourCode, setColourCode] = useState("");
  const [date, setDate] = useState("");
  const [originalDate, setOriginalDate] = useState("");
  const [originalColorname, setOriginalColorname] = useState("");

  const [lossPercent, setLossPercent] = useState(2);
  const [labourCost, setLabourCost] = useState(30);
  const [benefitPercent, setBenefitPercent] = useState(25);

  const [loading, setLoading] = useState(false);
  const [totalWeight, setTotalWeight] = useState(0);
  const [totalRMC, setTotalRMC] = useState(0);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setShowToastMessage] = useState("");
  const [formTouched, setFormTouched] = useState(false);
  const [batchSize, setBatchSize] = useState("");
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [inputValue, setInputValue] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const fetchMaterials = useCallback(async (search = "", pageNum = 1) => {
    setIsLoading(true);
    try {
      const res = await axios.get(`${BASE_URL}/materialMaster`, {
        params: {
          search, // server should filter based on this
          limit: 10,
          page: pageNum,
        },
        withCredentials: true,
      });

      const options = res.data.data.map((mat) => ({
        value: mat._id,
        label: `${mat.chemicalName} (${mat.chemicalCode})`,
        basePrice: mat.pricePerKg,
        ...mat,
      }));

      if (pageNum === 1) {
        setMaterials(options);
      } else {
        setMaterials((prev) => [...prev, ...options]);
      }

      setHasMore(res.data.data.length === 10);
    } catch (err) {
      console.error("Failed to fetch materials:", err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMaterials();
  }, []);

  const updateLocalState = () => {
    const copiedFormulation = cloneDeep(formulation); // 🔒 deep copy

    setColourName(copiedFormulation.colourName || "");
    setColourCode(copiedFormulation.colourCode || "");
    setDate(copiedFormulation.date || "");
    setOriginalDate(copiedFormulation.date || "");
    setOriginalColorname(copiedFormulation.colourName || "");
    setBenefitPercent(copiedFormulation.benefitPercent || 0);
    setLossPercent(copiedFormulation.lossPercent || 0);
    setLabourCost(copiedFormulation.labourCost || 0);

    const updatedMaterials =
      copiedFormulation.materials?.map((mat) => {
        const basePrice = parseFloat(mat.material?.basePrice || 0);
        const weight = parseFloat(mat.weight || 0);
        return {
          value: mat.material?._id || "",
          label: mat.material?.chemicalName
            ? `${mat.material.chemicalName} (${mat.material.chemicalCode})`
            : "",
          basePrice,
          weight,
          totalPrice: weight * basePrice,
          chemicalType: mat.material.chemicalType,
        };
      }) || [];

    setSelectedMaterials(updatedMaterials);

    setTotalWeight(
      updatedMaterials.reduce((sum, item) => sum + parseFloat(item.weight), 0)
    );

    setTotalRMC(
      updatedMaterials.reduce(
        (sum, item) =>
          sum + parseFloat(item.weight) * parseFloat(item.basePrice),
        0
      )
    );
  };
  useEffect(() => {
    if (selectedMaterials.length > prevLengthRef.current) {
      // new row added
      const lastIndex = selectedMaterials.length - 1;

      // scroll to top
      if (scrollContainerRef.current) {
        scrollContainerRef.current.scrollTop = 0;
      }

      // focus the new input
      const input = inputRefs.current[lastIndex];
      if (input) {
        input.focus();
        input.select();
      }
    }

    prevLengthRef.current = selectedMaterials.length;
  }, [selectedMaterials]);

  useEffect(() => {
    if (formulation) {
      updateLocalState();
    }
  }, [formulation]);

  useEffect(() => {
    if (actualId) fetchFormulations();
    else {
      dispatch(addSingleFormulation(defaultFormulation));
    }
  }, [actualId, defaultFormulation, dispatch]);

  const fetchFormulations = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${BASE_URL}/formulations/${actualId}`, {
        withCredentials: true,
      });
      dispatch(addSingleFormulation(response.data.data));
      setLoading(false);
    } catch (err) {
      setLoading(false);

      console.error("Error fetching material:", err);
    }
  };

  const handleSelect = (selected) => {
    if (
      selected &&
      !selectedMaterials.find((mat) => mat.value === selected.value)
    ) {
      setSelectedMaterials([
        ...selectedMaterials,
        { ...selected, weight: 0, totalPrice: 0 },
      ]);
    }
  };

  const updateWeightHandler = (updatedMaterial) => {
    const newTotalWeight = updatedMaterial.reduce(
      (sum, m) => sum + (parseFloat(m.weight) || 0),
      0
    );
    setTotalWeight(parseFloat(newTotalWeight.toFixed(5)));
  };
  const updateRMCHandler = (updatedMaterial) => {
    const totalBaseCost = updatedMaterial.reduce(
      (sum, mat) => sum + mat.totalPrice,
      0
    );
    setTotalRMC(totalBaseCost);
  };
  const handleRemove = (id) => {
    const updated = selectedMaterials.filter((mat) => mat.value !== id);
    setSelectedMaterials(updated);
    // Recalculate total weight
    updateWeightHandler(updated);

    //Recalculate total RMC
    updateRMCHandler(updated);
  };

  const updateWeightLocally = (index, weight) => {
    const updated = [...selectedMaterials];
    updated[index].weight = weight; // keep as string
    setSelectedMaterials(updated);
  };

  const finalizeWeight = (index, weight) => {
    const updated = [...selectedMaterials];
    const mat = updated[index];
    const parsedWeight = parseFloat(weight) || 0;
    mat.weight = weight; // keep original input
    mat.totalPrice = parsedWeight * mat.basePrice;
    setSelectedMaterials(updated);
    //calculate total weight
    updateWeightHandler(updated);

    //Recalculate total RMC
    updateRMCHandler(updated);
  };

  const handleBatchSizeChange = (e) => {
    const value = e.target.value;
    if (!/^\d{0,3}(\.\d{0,4})?$/.test(value)) return;
    setBatchSize(value);

    const batch = parseFloat(value);
    if (!batch || totalWeight === 0) return;

    const updated = selectedMaterials.map((mat) => {
      const originalWeight = parseFloat(mat.weight) || 0;
      const adjustedWeight = (batch * originalWeight) / totalWeight;
      const totalPrice = adjustedWeight * mat.basePrice;

      return {
        ...mat,
        weight: adjustedWeight.toFixed(5),
        totalPrice,
      };
    });

    setSelectedMaterials(updated);

    updateWeightHandler(updated);

    //Recalculate total RMC
    updateRMCHandler(updated);
  };

  const clearBatchSize = () => {
    setBatchSize("");

    updateLocalState();
  };

  // Sum up weights for the types present in selected materials
  const chemicalTypeWeights = selectedMaterials.reduce((acc, mat) => {
    const type = mat.chemicalType || "";
    const weight = parseFloat(mat.weight) || 0;
    acc[type] = (acc[type] || 0) + weight;
    return acc;
  }, {});

  // Create full list of types, filling in zeros for missing types
  const chemicalTypeData = chemicalTypes.map((type) => {
    const weight = chemicalTypeWeights[type] || 0;
    const percent = totalWeight > 0 ? (weight / totalWeight) * 100 : 0;
    return {
      type,
      weight,
      percent,
    };
  });
  console.log(
    "chemicalTypeWeights",
    chemicalTypeWeights,
    "chemicalTypeData",
    chemicalTypeData
  );
  const basePricePerKg = useMemo(() => {
    return totalWeight ? totalRMC / totalWeight : 0;
  }, [totalWeight, totalRMC]);

  let lossAmount = (basePricePerKg * lossPercent) / 100;
  let withLoss = basePricePerKg + (basePricePerKg * lossPercent) / 100;

  let withLabour = withLoss + parseFloat(labourCost || 0);
  let benefitRupee = (withLabour * benefitPercent) / 100;
  let finalPrice = withLabour + benefitRupee;

  const isFormValid =
    colourName &&
    colourCode &&
    date &&
    selectedMaterials.length > 0 &&
    selectedMaterials.every((mat) => mat.weight > 0);

  const handleSubmit = async () => {
    setFormTouched(true);
    if (!isFormValid) {
      // Just return without toast or alert
      return;
    }
    const payload = {
      colourName,
      colourCode,
      date,
      materials: selectedMaterials.map((mat) => ({
        material: mat.value,
        weight: mat.weight,
      })),
      lossPercent,
      labourCost,
      benefitPercent,
      finalPricePerKg: parseFloat(finalPrice.toFixed(2)),
    };

    try {
      setLoading(true);
      if (actualId) {
        let response;
        // Update existing material
        if (
          payload.date !== originalDate ||
          payload.colourName !== originalColorname
        ) {
          response = await axios.post(`${BASE_URL}/add-formulation`, payload, {
            withCredentials: true,
          });
          setLoading(false);
          setShowToast(true);
          setShowToastMessage("Formula is updated successfully");
          // dispatch(triggerRefresh());
        } else {
          response = await axios.patch(
            `${BASE_URL}/update-formulation/${actualId}`,
            payload,
            {
              withCredentials: true,
            }
          );
          setLoading(false);
          setShowToast(true);
          setShowToastMessage("Formula is updated successfully");
          // dispatch(addSingleFormulation(response.data.formulation));
        }
      } else {
        const res = await axios.post(`${BASE_URL}/add-formulation`, payload, {
          withCredentials: true,
        });
        setShowToast(true);
        setShowToastMessage("Formula is Saved successfully");
        setFormTouched(false);
        dispatch(addSingleFormulation(defaultFormulation));
        // dispatch(triggerRefresh());
      }
      // dispatch(triggerRefresh());

      // resetForm();
    } catch (err) {
      console.error("Submit error:", err);
      setShowToast(true);

      setShowToastMessage("Failed to submit formulation.");
    } finally {
      setLoading(false);
    }
  };

  const goBackToListHandler = () => {
    dispatch(triggerRefresh());
    goBackToList();
  };

  const validateInput = (value, pattern) => {
    const regex = new RegExp(pattern);
    return regex.test(value);
  };

  const handleScrollToBottom = () => {
    if (hasMore && !isLoading) {
      const nextPage = page + 1;
      setPage(nextPage);
      fetchMaterials(inputValue, nextPage);
    }
  };

  const debouncedFetchMaterials = useCallback(
    debounce((value) => {
      setPage(1);
      fetchMaterials(value, 1);
    }, 500), // 500ms debounce delay
    []
  );

  const handleInputChange = (newValue) => {
    setInputValue(newValue);
    debouncedFetchMaterials(newValue);
    return newValue;
  };
  if (loading)
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader size={12} text="Loading Application..." />
      </div>
    );

  return (
    <div className="space-y-3 p-4">
      <Toast
        message={toastMessage}
        visible={showToast}
        onClose={() => setShowToast(false)}
      />
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-semibold">Formulation Details</h2>
        <div className="col-span-2 gap-1 flex justify-end">
          {!batchSize && (
            <button
              onClick={handleSubmit}
              className={`mt-4 px-6 py-2 rounded text-white ${"bg-pink-600 hover:bg-pink-700"}`}
            >
              {actualId ? "Update formulation" : "Create Formulation"}
            </button>
          )}
          <button
            onClick={goBackToListHandler}
            className="bg-pink-600 text-white px-4 mt-4 py-2 rounded hover:bg-pink-700 transition"
          >
            Back
          </button>
        </div>
      </div>
      {/* Colour Inputs */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div>
          <label className="mb-1 text-sm font-medium text-gray-700">
            Colour Name
          </label>
          <input
            type="text"
            value={colourName}
            onChange={(e) => setColourName(e.target.value)}
            placeholder="Colour Name"
            className={`border p-2 rounded w-full ${
              formTouched && !colourName ? "border-red-500" : ""
            }`}
            required
          />
          {formTouched && !colourName && (
            <p className="text-red-500 text-sm mt-1">Colour Name is required</p>
          )}
        </div>
        <div>
          <label className="mb-1 text-sm font-medium text-gray-700">
            Product Code
          </label>

          <input
            type="text"
            value={colourCode}
            onChange={(e) => setColourCode(e.target.value.toUpperCase())}
            placeholder="Colour Code"
            className={`border p-2 rounded w-full ${
              formTouched && !colourCode ? "border-red-500" : ""
            }`}
            required
          />
          {formTouched && !colourCode && (
            <p className="text-red-500 text-sm mt-1">
              Colour Code is required.
            </p>
          )}
        </div>
        <div>
          <label className="mb-1 text-sm font-medium text-gray-700">Date</label>
          <input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className={`border p-2 rounded w-full ${
              formTouched && !colourCode ? "border-red-500" : ""
            }`}
            required
          />
          {formTouched && !date && (
            <p className="text-red-500 text-sm mt-1">Date is required.</p>
          )}
        </div>
      </div>
      <div className="flex flex-wrap gap-6 w-full">
        {/* Material Select */}
        <div className="flex flex-col basis-[40%] min-w-[250px]">
          <label className="mb-1 text-sm font-medium text-gray-700">
            Material
          </label>
          <Select
            options={materials}
            onChange={handleSelect}
            onInputChange={handleInputChange}
            onMenuScrollToBottom={handleScrollToBottom}
            placeholder="Search and select material..."
            isClearable
            isLoading={isLoading}
            className="text-sm"
          />
        </div>

        {/* Batch Size Input */}
        {actualId && (
          <div className="flex flex-col basis-[30%] min-w-[200px]">
            <label className="mb-1 text-sm font-medium text-gray-700">
              Batch Size
            </label>
            <div className="relative w-full">
              <input
                type="text"
                value={batchSize}
                onChange={(e) => setBatchSize(e.target.value)}
                onBlur={handleBatchSizeChange}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    handleBatchSizeChange(e);
                  }
                }}
                className="w-full border border-gray-300 px-3 py-2 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 pr-8"
                placeholder="Enter batch size"
              />
              {batchSize && (
                <button
                  type="button"
                  onClick={clearBatchSize}
                  className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 text-sm"
                >
                  ×
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Material Table */}
      {selectedMaterials.length > 0 && (
        <div className="mt-4 border rounded p-4 bg-white shadow">
          <div className="grid grid-cols-[2fr_1fr_1fr_1fr_40px] gap-4 font-semibold border-b pb-2 text-sm">
            <div>Material</div>
            <div>Base Price/kg</div>
            <div>Weight (kg)</div>
            <div>RMC</div>
            <div className="text-right">Remove</div>
          </div>
          <div
            ref={scrollContainerRef}
            className="max-h-[300px] overflow-y-auto pr-2"
          >
            {selectedMaterials.map((mat, i) => (
              <div
                key={mat.value}
                className="grid grid-cols-[2fr_1fr_1fr_1fr_40px] gap-4 items-center border-b py-2 text-sm"
              >
                <div className="truncate">{mat.label}</div>
                <div>₹{mat.basePrice}</div>
                <div>
                  <input
                    type="text"
                    ref={(el) => {
                      inputRefs.current[i] = el;
                    }}
                    value={mat.weight}
                    onChange={(e) => {
                      const val = e.target.value;
                      if (
                        val === "" ||
                        validateInput(val, /^\d{0,4}(\.\d{0,5})?$/)
                      ) {
                        updateWeightLocally(i, val); // only update UI state
                      }
                    }}
                    onBlur={(e) => {
                      const val = e.target.value;
                      finalizeWeight(i, val); // now safely parseFloat and calculate
                    }}
                    className="border p-1 w-[100px] text-right rounded"
                    placeholder="0.0000"
                  />
                </div>
                <div>₹{mat.totalPrice.toFixed(2)}</div>
                <div className="text-right">
                  <button
                    onClick={() => handleRemove(mat.value)}
                    className="text-red-500 hover:text-red-700"
                    aria-label="Remove"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="w-5 h-5"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={2}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M6 7h12M9 7V4h6v3M10 11v6m4-6v6M5 7h14l-1 12a2 2 0 01-2 2H8a2 2 0 01-2-2L5 7z"
                      />
                    </svg>
                  </button>
                </div>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-[2fr_1fr_1fr_1fr_40px] gap-4 font-extrabold border-b pb-2 text-sm align-middle">
            <div>Total</div>
            <div></div>
            <div>{totalWeight.toFixed(5)} kg</div>
            <div>₹{totalRMC.toFixed(4)}</div>
            <div></div>
          </div>
          <div className="flex flex-wrap gap-12 justify-between ">
            <div className="mt-6 space-y-4 text-sm flex-1 min-w-[300px] max-w-[600px]">
              <div>
                Base Price per kg :{" "}
                <span className="font-extrabold">
                  ₹{basePricePerKg.toFixed(2)}
                </span>
              </div>
              <div className="flex items-center gap-2">
                Loss %
                <input
                  type="text"
                  value={lossPercent}
                  onChange={(e) => {
                    const val = e.target.value;
                    if (
                      val === "" ||
                      validateInput(val, /^\d{0,2}(\.\d{0,2})?$/)
                    ) {
                      setLossPercent(val);
                    }
                  }}
                  className="border p-1 rounded w-[70px] text-right"
                  placeholder="0.00"
                />
                <div className="flex gap-2">
                  Loss -{" "}
                  <span className="font-extrabold">
                    {" "}
                    ₹{lossAmount.toFixed(2)}
                  </span>
                  RMC + Loss % -
                  <span className="font-extrabold">
                    {" "}
                    ₹{withLoss.toFixed(2)}
                  </span>
                </div>
              </div>
              <div className="flex items-center gap-2">
                Labour Cost
                <input
                  type="text"
                  value={labourCost}
                  onChange={(e) => {
                    const val = e.target.value;
                    if (val === "" || validateInput(val, /^\d{0,3}(\.\d*)?$/)) {
                      setLabourCost(val);
                    }
                  }}
                  className="border p-1 rounded w-[70px] text-right"
                  placeholder="e.g. 100"
                />
                <span className="font-extrabold">
                  {" "}
                  ₹{withLabour.toFixed(2)}
                </span>
              </div>
              <div className="flex items-center gap-2 font-extrabold">
                Total Cost ₹{withLabour.toFixed(2)}
              </div>
              <div className="flex items-center gap-2">
                Profit %
                <input
                  type="text"
                  value={benefitPercent}
                  onChange={(e) => {
                    const val = e.target.value;
                    if (
                      val === "" ||
                      validateInput(val, /^\d{0,2}(\.\d{0,2})?$/)
                    ) {
                      setBenefitPercent(val);
                    }
                  }}
                  className="border p-1 rounded w-[70px] text-right"
                  placeholder="e.g. 10.50"
                />
                <div className="flex gap-2">
                  Profit Amount -{" "}
                  <span className="font-extrabold">
                    ₹ {benefitRupee.toFixed(2)}
                  </span>
                  Final Price -
                  <span className="font-extrabold">
                    {" "}
                    ₹{finalPrice.toFixed(2)} / kg
                  </span>
                </div>
              </div>
              <div className="font-extrabold">
                {" "}
                Final Cost after profit: ₹{finalPrice.toFixed(2)} / kg
              </div>
            </div>

            <div className="w-full sm:w-auto min-w-[320px]">
              {selectedMaterials.length > 0 && (
                <div className="mt-6 text-sm">
                  <div className="font-semibold mb-2">
                    Chemical Type Breakdown:
                  </div>
                  <div className="grid grid-cols-3 gap-4 text-gray-800 text-sm">
                    <div className="font-bold text-black-700">
                      Chemical Type
                    </div>
                    <div className="font-bold text-black-700 text-right">
                      Weight (kg)
                    </div>
                    <div className="font-bold text-black-700 text-right">
                      Weight %
                    </div>
                    {chemicalTypeData.map((item) => (
                      <React.Fragment key={item.type}>
                        <div className="font-medium">{item.type}</div>
                        <div className="text-right">
                          {item.weight.toFixed(5)}
                        </div>
                        <div className="text-right">
                          {item.percent.toFixed(2)}%
                        </div>
                      </React.Fragment>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Submit Button */}
    </div>
  );
};

export default FormulationDetail;
